import React, { useCallback, useEffect, useMemo } from "react";
import PersonalDataBox from "../components/checkout/PersonalDataBox";
import PaymentMethodBox from "../components/checkout/PaymentMethodBox";
import OrderSummaryBox from "../components/checkout/OrderSummaryBox";
import WhatsAppSupport from "../components/checkout/WhatsAppSupport";
import Header from "../components/checkout/Header";
import { useOidcAuth } from "../auth/OidcAuthProvider";
import { useMediaQuery } from "../hooks/useMediaQuery";
import gtag from "ga-gtag";
import { fetchProfile } from "@/utils/encrypt";

const CheckoutProPage: React.FC = () => {
  const { user, loginWithPassword, logout } = useOidcAuth();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const isLoggedIn = !!(user && user.access_token);
  const userName =
    user?.profile?.name || user?.profile?.preferred_username || "";

  const userEmail = useMemo(() => {
    return user?.profile?.email || "";
  }, [user]);

  const productId = process.env["APP_PRODUCT_ID_PRO"] || "";

  // Payment item configuration
  const paymentItem = {
    id: productId,
    title: "DIO Pro Vitalício",
    quantity: 1,
    unitPrice: 41880,
  };

  const handlePasswordLogin = useCallback(
    async (email: string, password: string) => {
      try {
        const success = await loginWithPassword(email, password);
        return success;
      } catch (error) {
        console.error("Error during password login:", error);
        return false;
      }
    },
    [loginWithPassword]
  );

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }, [logout]);

  const logo = useCallback(
    () => (
      <img
        src="https://hermes.dio.me/assets/products/full/pro-lifetime.svg"
        alt="DIO Pro Vitalício"
        className="w-64 h-auto mx-auto"
      />
    ),
    []
  );

  useEffect(() => {
    const encryptProfile = fetchProfile(user?.access_token || "", "global");
    const event = {
      product: "pro_vitalicio",
      item_id: "pro_vitalicio",
      item_name: "PRO VITALÍCIO",
      affiliation: "plano",
      pageType: "internal",
      ...encryptProfile
    };
    gtag("event", "checkout_begin", event);
  }, [gtag]);

  return (
    <div className="min-h-screen bg-[#F7F9FB] flex flex-col justify-start">
      <Header
        defaultLogoUrl={
          isMobile
            ? "https://hermes.dio.me/assets/products/full/pro-lifetime.svg"
            : "https://hermes.dio.me/assets/diome/logo-full.svg"
        }
      />
      <div className="flex justify-center items-start py-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-x-8 lg:gap-y-0 w-full max-w-6xl">
          <div className={`${isLoggedIn ? "order-3 lg:order-1" : "order-1"}`}>
            <PersonalDataBox
              isLoggedIn={isLoggedIn}
              userName={userName}
              userEmail={userEmail}
              onLoginAttempt={handlePasswordLogin}
              onLogout={handleLogout}
            />
          </div>
          <div className="order-2">
            <PaymentMethodBox
              user={user}
              isLoggedIn={isLoggedIn}
              onLogout={handleLogout}
              paymentItem={paymentItem}
              product="pro"
            />
          </div>
          <div
            className={`${
              isLoggedIn ? "order-1 lg:order-3" : "order-2 lg:order-3"
            }`}
          >
            <OrderSummaryBox
              logo={logo}
              planName="Vitalício"
              productPrice="R$ 1198,80"
              discount="-R$ 780,00"
              total="R$ 418,80"
              installment="12x R$ 34,90"
              features={[
                "Acesso ilimitado em bootcamps e os próximos lançamentos.",
                "Acesso a TODOS os cursos DIO PRO e próximos lançamentos nas carreiras front-end, back-end, mobile, data, inteligência artificial, games, cloud e DevOps.",
                "Acesso a TODAS as formações profissionais exclusivas do DIO PRO e próximos lançamentos para você se preparar e conquistar as melhores vagas do mercado tech.",
                "+200 projetos para você ganhar experiência prática e os próximos lançamentos.",
                "Certificado de conclusão.",
                "Acesso ao AI Job Hunter com 5.000 créditos, indicação diária de vagas com score de compatibilidade e feedback personalizado por vaga.",
                "Acesso ao Agente de IA que adapta seu currículo ao ATS, gera o Raio-X de cada vaga e mostra o passo a passo de evolução para chegar preparado na entrevista.",
                "Acesso ao Preparatório de entrevista que reproduz uma situação real com recrutadores e feedback personalizado sobre o seu desempenho na entrevista.",
                "Cancelamento grátis em 7 dias."
              ]}
            />
          </div>
        </div>
      </div>

      {!isMobile && (
        <WhatsAppSupport
          delay={5000}
          name="Raquel Leão"
          message="Olá! 😊&#10;Precisa de ajuda para realizar sua matrícula?"
        />
      )}
    </div>
  );
};

export default CheckoutProPage;
